import { useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';
import { addSiteRecce } from '@api/services/site-recce.service';
import { AssetTypeEnum, SiteRecceTypeEnum, SiteReccePriorityEnum } from '@api/enum';

export type TSiteRecceFormValues = {
    remarks: string;
    preferredDate: string;
    priority: SiteReccePriorityEnum;
    type: SiteRecceTypeEnum | null;
    assetType: AssetTypeEnum | null;
    poRecieved: boolean;
    quantity?: number;
    files: File[];
};

export const useSiteRecceAddForm = (clientInstallationId: string, projectInstallationId: string) => {
    const schema = Yup.object({
        remarks: Yup.string().required('Remarks are required'),
        preferredDate: Yup.string().nullable(),
        priority: Yup.mixed<SiteReccePriorityEnum>()
            .oneOf(Object.values(SiteReccePriorityEnum))
            .required('Priority is required'),
        type: Yup.mixed<SiteRecceTypeEnum>()
            .oneOf([...Object.values(SiteRecceTypeEnum), null])
            .nullable(),
        assetType: Yup.mixed<AssetTypeEnum>()
            .oneOf([...Object.values(AssetTypeEnum), null])
            .nullable(),
        poRecieved: Yup.boolean().required('PO status is required'),
    });

    const initialValues: TSiteRecceFormValues = {
        remarks: '',
        preferredDate: '',
        priority: SiteReccePriorityEnum.LOW,
        type: null,
        assetType: null,
        poRecieved: false,
        quantity: 0,
        files: [],
    };

    const mutation = useMutation((payload: TSiteRecceFormValues) => {
        const formData = new FormData();
        formData.append('remarks', payload.remarks);
        formData.append('preferredDate', payload.preferredDate || '');
        formData.append('priority', payload.priority);
        formData.append('poRecieved', String(payload.poRecieved));
        if (payload.type !== null) formData.append('type', payload.type);
        if (payload.assetType !== null) formData.append('assetType', payload.assetType);
        if (payload.quantity !== undefined) formData.append('quantity', String(payload.quantity));

        payload.files.forEach((file) => {
            formData.append('files', file);
        });

        return addSiteRecce(clientInstallationId, projectInstallationId, formData);
    });

    const submit = async (values: TSiteRecceFormValues) => {
        const { data } = await mutation.mutateAsync(values);
        return data;
    };

    return {
        initialValues,
        schema,
        isSubmitting: mutation.isLoading,
        submit,
    };
};
